import { useCallback, useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import Dialog, { DialogPrimaryButton } from './Dialog';
import InputField from './InputField';
import TextButton from './TextButton';
import SvgIcon from './SvgIcon';
import api from '../services/api';
import { showGlobalToast } from '../services/toastBus';
import { ReactComponent as TrashIcon } from '../assets/icons/24/remove.svg';

const IdentifiersList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginBottom: 16,
});

const IdentifierRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const RemoveButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  color: '#ACACAC',
  '&:hover': {
    color: '#FF6B6B',
  },
});

const readServiceIdentifiers = (service) => {
  if (Array.isArray(service?.identifiers)) {
    const values = service.identifiers.map((id) => String(id ?? '').trim()).filter(Boolean);
    return values.length ? values : [''];
  }

  const legacy = service?.identifier ?? service?.externalId ?? service?.platformUserId ?? '';
  const trimmed = String(legacy).trim();
  return trimmed ? [trimmed] : [''];
};

const ServiceIdentifierDialog = ({ open, onClose, service, platformLabel, onSaved }) => {
  const [values, setValues] = useState(['']);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && service) {
      setValues(readServiceIdentifiers(service));
    }
  }, [open, service]);

  const handleClose = useCallback(() => {
    setValues(['']);
    onClose();
  }, [onClose]);

  const handleValueChange = useCallback((index, nextValue) => {
    setValues((prev) => prev.map((value, i) => (i === index ? nextValue : value)));
  }, []);

  const handleAdd = useCallback(() => {
    setValues((prev) => [...prev, '']);
  }, []);

  const handleRemove = useCallback((index) => {
    setValues((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!service?.id) return;
    const identifiers = values.map((value) => value.trim()).filter(Boolean);
    setSubmitting(true);
    const response = await api.patch(`/services/${service.id}/identifier`, { identifiers });
    setSubmitting(false);
    if (!response?.ok) return;
    showGlobalToast('Identifiants enregistrés', 'success');
    onSaved?.();
    handleClose();
  }, [service?.id, values, onSaved, handleClose]);

  if (!service) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={`IDENTIFIANTS ${platformLabel?.toUpperCase?.() ?? ''}`}
    >
      <IdentifiersList>
        {values.map((value, index) => (
          <IdentifierRow key={index}>
            <InputField
              label={`Identifiant ${values.length > 1 ? index + 1 : ''}`.trim()}
              value={value}
              onChange={(e) => handleValueChange(index, e.target.value)}
              fullWidth
              onClear={value ? () => handleValueChange(index, '') : undefined}
            />
            <RemoveButton onClick={() => handleRemove(index)} aria-label="Supprimer l'identifiant">
              <SvgIcon icon={TrashIcon} color="inherit" size={24} />
            </RemoveButton>
          </IdentifierRow>
        ))}
      </IdentifiersList>
      <TextButton onClick={handleAdd} sx={{ mb: 3, fontSize: 12, fontWeight: 700 }}>
        AJOUTER UN IDENTIFIANT
      </TextButton>
      <DialogPrimaryButton variant="contained" onClick={handleSubmit} disabled={submitting}>
        ENREGISTRER
      </DialogPrimaryButton>
    </Dialog>
  );
};

export default ServiceIdentifierDialog;
